import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { readPref, writePref } from "../../utils/prefs";
import { useLanguage } from "../../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const PREF_KEY = "idb_chat_session";
const LEGACY_LS_KEY = "alterego_chat_session";

function readInitialSession() {
  const fromCookie = readPref(PREF_KEY, "");
  if (fromCookie) return fromCookie;
  try {
    const legacy = localStorage.getItem(LEGACY_LS_KEY);
    if (legacy) {
      writePref(PREF_KEY, legacy);
      try { localStorage.removeItem(LEGACY_LS_KEY); } catch (err) {
        console.warn("[useChatSession] removeItem legacy key failed:", err);
      }
      return legacy;
    }
  } catch (err) {
    console.warn("[useChatSession] legacy localStorage read failed:", err);
  }
  return "";
}

/**
 * useChatSession — owns the chat session state + all network calls.
 *
 * Responsibilities:
 *  - persist / migrate session id via cookie (idb_chat_session)
 *  - load conversation history on demand
 *  - send a user message and append the assistant reply
 *  - clear the current session (both local + remote)
 *
 * Exposes a ``sendHandlers`` style API that the UI can bind directly.
 */
export default function useChatSession({ enabled, loadTrigger }) {
  const { t, lang } = useLanguage();
  const [sessionId, setSessionId] = useState(readInitialSession);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const loadHistory = useCallback(async (sid) => {
    if (!sid) return;
    setLoadingHistory(true);
    try {
      const { data } = await axios.get(`${API}/chat/history/${sid}`, { withCredentials: true });
      setMessages(data);
    } catch (err) {
      console.warn("[useChatSession] loadHistory failed:", err);
      setMessages([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Hydrate once when the panel opens with an existing session.
  useEffect(() => {
    if (enabled && sessionId && messages.length === 0) loadHistory(sessionId);
  }, [enabled, sessionId, messages.length, loadHistory, loadTrigger]);

  const send = useCallback(async (text) => {
    const msg = (text || "").trim();
    if (!msg || sending) return;
    const nowIso = new Date().toISOString();
    setMessages(m => [...m, { role: "user", text: msg, created_at: nowIso }]);
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/chat`,
        { message: msg, session_id: sessionId || undefined, lang },
        { withCredentials: true, timeout: 90000 },
      );
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        writePref(PREF_KEY, data.session_id);
      }
      setMessages(m => [...m, {
        role: "assistant",
        text: data.reply,
        created_at: data.created_at,
        sources: data.sources,
      }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || t("chat.error");
      setMessages(m => [...m, {
        role: "assistant",
        text: `⚠ ${detail}`,
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
    }
  }, [sending, sessionId, lang, t]);

  const clear = useCallback(async () => {
    if (!sessionId) { setMessages([]); return; }
    try {
      await axios.delete(`${API}/chat/session/${sessionId}`, { withCredentials: true });
    } catch (err) {
      // Remote delete failure is non-blocking — we still clear local state.
      console.warn("[useChatSession] remote delete failed:", err);
    }
    writePref(PREF_KEY, "", 0);
    setSessionId("");
    setMessages([]);
  }, [sessionId]);

  return {
    sessionId,
    messages,
    sending,
    loadingHistory,
    send,
    clear,
  };
}

// Ref-based scroll-to-bottom helper, so the component can keep the list pinned.
export function useAutoScroll(deps) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, deps);
  return ref;
}
